import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  UploadContainer,
  UploadArea,
  UploadIcon,
  UploadContent,
  UploadText,
  UploadHint,
} from './FileUploader.styles'
import type { FileUploaderProps } from './FileUploader.types'

export const FileUploader: React.FC<FileUploaderProps> = ({
  onUpload,
  onReject,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.dwg,.dxf,.webp',
  maxSize = 50 * 1024 * 1024, // 50MB default
  multiple = false,
}) => {
  const { t } = useTranslation('ui')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (!files) return

    if (multiple) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.size <= maxSize) {
          await onUpload(file)
        } else {
          onReject?.(file, 'size')
        }
      }
    } else {
      const file = files[0]
      if (file) {
        if (file.size <= maxSize) {
          await onUpload(file)
        } else {
          onReject?.(file, 'size')
        }
      }
    }

    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault()
    setDragOver(false)

    const files = event.dataTransfer.files
    if (!files) return

    if (multiple) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.size <= maxSize) {
          await onUpload(file)
        } else {
          onReject?.(file, 'size')
        }
      }
    } else {
      const file = files[0]
      if (file && file.size <= maxSize) {
        await onUpload(file)
      } else if (file) {
        onReject?.(file, 'size')
      }
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  return (
    <UploadContainer>
      <UploadArea
        role="button"
        tabIndex={0}
        className={dragOver ? 'drag-over' : ''}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={(event) => {
          if (event.target !== fileInputRef.current)
            fileInputRef.current?.click()
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          fileInputRef.current?.click()
        }}
      >
        <UploadIcon>
          <svg viewBox="0 0 24 24">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
        </UploadIcon>
        <UploadContent>
          <UploadText>{t('files.uploadText')}</UploadText>
          <UploadHint>
            {t('files.uploadHint', { maxSize: maxSize / 1024 / 1024 })}
          </UploadHint>
        </UploadContent>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          multiple={multiple}
          accept={accept}
        />
      </UploadArea>
    </UploadContainer>
  )
}
